import { ReactNode, createContext, useContext, useEffect } from "react";
import styles from "./Tabs.module.css";
import Tooltip from "../ui/Tooltip";

type initialValues = {
  value: string;
  onChange: (tabKey: string) => void;
};

const TabContext = createContext<initialValues>({
  value: "",
  onChange: () => {},
});

function Tab({
  children,
  value,
  onChange,
}: {
  children: ReactNode;
  value: string;
  onChange: (tabKey: string) => void;
}) {
  return (
    <div>
      <TabContext.Provider value={{ value, onChange }}>
        {children}
      </TabContext.Provider>
    </div>
  );
}

function Heads({ children }: { children: ReactNode }) {
  return <div className={styles["heads"]}>{children}</div>;
}

function Item({
  label,
  tabKey,
  disabled = false,
  hasTooltip,
  content,
}: {
  tabKey: string;
  label: string;
  disabled?: boolean;
  hasTooltip?: boolean;
  content?: string;
}) {
  const { value, onChange } = useContext(TabContext);
  const handleClick = () => {
    onChange(tabKey);
  };

  if (hasTooltip) {
    if (disabled) {
      return (
        <Tooltip content={content}>
          <div
            className={`${styles["item"]} ${disabled ? styles["tab__disabled"] : ""}`}
          >
            {label}
          </div>
        </Tooltip>
      );
    }

    return (
      <Tooltip content={content}>
        <div
          onClick={handleClick}
          className={`${styles["item"]} ${tabKey === value ? styles["active"] : ""} ${disabled ? styles["disabled"] : ""}`}
        >
          {label}
        </div>
      </Tooltip>
    );
  }

  if (disabled) {
    return (
      <div
        className={`${styles["item"]} ${disabled ? styles["tab__disabled"] : ""}`}
      >
        {label}
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      className={`${styles["item"]} ${tabKey === value ? styles["active"] : ""} ${disabled ? styles["disabled"] : ""}`}
    >
      {label}
    </div>
  );
}

function ContentWrapper({ children }: { children: ReactNode }) {
  return <div className="contentWraper">{children}</div>;
}

function Content({
  children,
  tabKey,
}: {
  children: ReactNode;
  tabKey: string;
  disabled?: boolean;
}) {
  const { value } = useContext(TabContext);
  return value === tabKey ? (
    <div className={styles["tab__content"]}>{children}</div>
  ) : null;
}

Tab.Heads = Heads;
Tab.Item = Item;
Tab.ContentWrapper = ContentWrapper;
Tab.Content = Content;

export default Tab;
